import React, { useCallback, useState } from "react";
import { useFormikContext } from "formik";
import Modal, { ModalConfirmation } from "@adsk/alloy-react-modal";
import { IssueTemplate } from "@adsk/acc-issues-types";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import { useLocalStorage } from "@adsk/acc-issues-core/utils/hooks/useLocalStorage";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { IssueTemplatesDropdown } from "../input-field-components/IssueTemplatesDropdown";
import { getFnForGetIssueTemplateById } from "@adsk/acc-issues-core/state/redux/ducks/issueTemplates";
import { getIssueFormAttributesFromTemplate } from "../utils/data/getIssueAttributesFromTemplate";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { useIncrementor } from "../utils/hooks/useIncrementor";
import { useGetTemplateTransformedForCurrentUser } from "../utils/hooks/useGetTemplateTransformedForCurrentUser";
import { useLocalizedDecimalSeparator } from "@adsk/acc-issues-core/utils/hooks/useLocalizedDecimalSeparator";
import { getIsMemberOptionDisabledDueToPermissions } from "@adsk/acc-issues-common/utils/user/projectMemberUtils";
import { ProjectMemberDropdownOption } from "@adsk/acc-issues-common/types/projectMember";
import { getCurrentUserPermittedActionsForNewIssue } from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import { PermittedAction } from "@adsk/acc-issues-types";

export const IssueTemplatesField = ({
  onTemplateChanged,
  shouldIncludeMemberFn,
}: {
  onTemplateChanged: () => void;
  shouldIncludeMemberFn?: (option: ProjectMemberDropdownOption) => boolean;
}) => {
  const { values, setFieldValue, setFieldTouched, resetForm, dirty } =
    useFormikContext<IssueFormAttributes>();
  const getTemplateById = useSelector(getFnForGetIssueTemplateById);
  const { localizeNumericCustomFields } = useLocalizedDecimalSeparator();
  const [, setLastSelectedTypeId] = useLocalStorage(
    LocalStorageKeys.LAST_SELECTED_TYPE_ID
  );
  const { T } = useTranslations();
  const [pendingTemplateId, setPendingTemplateId] =
    useState<string | undefined>(undefined);
  const [key, incrementKey] = useIncrementor();
  const getTemplateTransformedForCurrentUser =
    useGetTemplateTransformedForCurrentUser();

  const permittedActions = useSelector(
    getCurrentUserPermittedActionsForNewIssue
  );
  const isUserAllowedToAssignAll = permittedActions.includes(
    PermittedAction.AssignAll
  );

  const handleTemplateChange = useCallback(
    (templateId: string | undefined) => {
      const fieldName = "templateId";
      setFieldValue(fieldName, templateId || "");
      setFieldTouched(fieldName, true, false);
      if (!templateId) {
        // templateId is undefined when the `clear` button is clicked.
        // in that case, we just want to empty the template field, but not change
        // any of the other fields
        onTemplateChanged && onTemplateChanged();
        return;
      }
      const templateFromState: IssueTemplate = getTemplateById(templateId);

      if (templateFromState) {
        // If user doesn't have assignAll permission we remove watchers from different companies
        const isWatcherOptionDisabled =
          getIsMemberOptionDisabledDueToPermissions(shouldIncludeMemberFn);
        const watcherObjects =
          templateFromState.issueAttributes.watcherObjects?.filter(
            ({ id: value, type: memberType }) =>
              isUserAllowedToAssignAll ||
              !isWatcherOptionDisabled({ value, memberType })
          );

        const templateWithPermittedWatchers: IssueTemplate = {
          ...templateFromState,
          issueAttributes: {
            ...templateFromState.issueAttributes,
            watcherObjects,
          },
        };

        const template = getTemplateTransformedForCurrentUser({
          template: templateWithPermittedWatchers,
        });

        const issueFormAttributesFromTpl =
          getIssueFormAttributesFromTemplate(template);
        localizeNumericCustomFields(
          issueFormAttributesFromTpl.customAttributes
        );

        issueFormAttributesFromTpl.templateId = templateId;
        setLastSelectedTypeId(template.issueAttributes.typeId);
        resetForm({ values: issueFormAttributesFromTpl });
        onTemplateChanged && onTemplateChanged();
      } else {
        console.error(
          "Template data could not be loaded: this should not occur",
          templateId
        );
      }
    },
    [
      setFieldValue,
      setFieldTouched,
      getTemplateById,
      onTemplateChanged,
      getTemplateTransformedForCurrentUser,
      localizeNumericCustomFields,
      setLastSelectedTypeId,
      resetForm,
      shouldIncludeMemberFn,
      isUserAllowedToAssignAll,
    ]
  );
  return (
    <>
      <FieldWrapper
        key={key}
        label={T.translate("issueCreation.template.fieldTitle")}
        testId="issue-template-field"
      >
        <IssueTemplatesDropdown
          selectedIssueTemplateId={values.templateId}
          onChange={(templateId: string | undefined) => {
            if (!dirty || !templateId) {
              handleTemplateChange(templateId);
            } else {
              setPendingTemplateId(templateId);
            }
          }}
        />
      </FieldWrapper>
      <ModalConfirmation
        style={{}}
        show={!!pendingTemplateId}
        size={Modal.SIZES.MEDIUM}
        onConfirm={() => {
          if (pendingTemplateId) {
            handleTemplateChange(pendingTemplateId);
          }
          setPendingTemplateId(undefined);
          incrementKey();
        }}
        onClose={() => {
          setPendingTemplateId(undefined);
          // if we don't trigger a reinstantiation here, the component will render without
          // the placeholder in certain situations (namely: if the form was edited, then the template cleared,
          // then a new template selected, triggering this modal, then cancelled/closed)
          // .. this is all due to the internal state of the alloy component.
          // And reinstantiation is the way to fix weird internal states of components...
          incrementKey();
        }}
        title={T.translate("issueCreation.template.confirmationModalTitle")}
        message={T.translate(
          "issueCreation.template.confirmationModalDescription"
        )}
        confirmationButtonLabel={
          T.translate(
            "issueCreation.template.confirmationModalButton"
          ) as string
        }
      />
    </>
  );
};
